import { type CSSProperties, type ReactNode } from "react"
import moment from "moment"
import { MessageCircle, PackagePlus } from "lucide-react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { Project } from "../../../types/project.type"
import { RentalRequest } from "../../../types/rental.type"
import {
  getReadablePrimaryButtonStyle,
  resolveThemeColor,
} from "../../../utils/buttonColor"
import { currencyFormatter } from "../../../utils/formatter"

/** What the customer sends over WhatsApp so staff can find the request. */
function rentalRequestMessage(request: RentalRequest, project: Project, locale: string) {
  const lines = [
    `Hi ${project.name}, I submitted a rental request.`,
    ``,
    `Ref: ${request.leaseCode}`,
    `Item: ${request.asset?.name ?? '-'}`,
    `From: ${moment(request.startDate).locale(locale).format('DD MMM YYYY')}`,
    `Until: ${moment(request.endDate).locale(locale).format('DD MMM YYYY')}`,
    `Name: ${request.tenant?.name ?? '-'}`,
    `Phone: ${request.tenant?.phoneNo ?? '-'}`,
  ]
  return lines.join('\n')
}

export function RentConfirmationSection({
  request,
  project,
}: {
  request: RentalRequest
  project: Project
}) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('rent')

  const locale = i18n.language?.startsWith('id') ? 'id' : 'en'
  const themeColor = resolveThemeColor(project.themeColor)
  const coverUrl = project.coverImageFiles?.[0]?.s3Url

  const backgroundStyles: CSSProperties = coverUrl
    ? {
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.22) 0%, rgba(15, 23, 42, 0.4) 100%), linear-gradient(135deg, ${themeColor}18 0%, transparent 50%), url(${JSON.stringify(coverUrl)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {
        background: `linear-gradient(160deg, ${themeColor} 0%, ${themeColor}dd 45%, ${themeColor}aa 100%)`,
      }

  const waDigits = project.contactNo?.replace(/\D/g, "") ?? ""
  const waHref = waDigits
    ? `https://wa.me/${waDigits}?text=${encodeURIComponent(rentalRequestMessage(request, project, locale))}`
    : undefined

  const detailRows: { label: string; value: ReactNode }[] = [
    { label: t('confirmation.reference'), value: request.leaseCode },
    { label: t('fields.item'), value: request.asset?.name ?? '-' },
    {
      label: t('fields.startDate'),
      value: moment(request.startDate).locale(locale).format('DD MMM YYYY'),
    },
    {
      label: t('fields.returnDate'),
      value: moment(request.endDate).locale(locale).format('DD MMM YYYY'),
    },
    {
      label: t('fields.total'),
      value: (
        <>
          {currencyFormatter(request.totalAmount)}
          {request.depositAmount > 0 ? (
            <span className="mt-0.5 block text-xs font-normal text-gray-500">
              {t('includesDeposit', { amount: currencyFormatter(request.depositAmount) })}
            </span>
          ) : null}
        </>
      ),
    },
    { label: t('fields.name'), value: request.tenant?.name ?? '-' },
    { label: t('fields.phone'), value: request.tenant?.phoneNo ?? '-' },
  ]

  return (
    <div className="relative min-h-screen" style={backgroundStyles}>
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/12 to-transparent md:from-black/18"
        aria-hidden
      />

      <div className="relative z-[1] mx-auto flex max-w-3xl flex-col gap-6 px-5 pb-16 pt-10 md:pb-24 md:pt-14">
        <section
          className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white/95 text-gray-900 shadow-xl shadow-gray-900/10 backdrop-blur-sm"
          style={{
            boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 0 0 1px ${themeColor}12`,
          }}
        >
          <div
            className="border-b border-gray-100 px-6 py-6 text-center sm:px-8 sm:py-8"
            style={{
              background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
            }}
          >
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: themeColor }}
            >
              {t('confirmation.badge')}
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 md:text-3xl">
              {t('confirmation.thanks')}
            </h1>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-gray-600">
              {t('confirmation.body')}
            </p>
          </div>

          <div className="p-6 sm:p-8">
            <dl className="divide-y divide-gray-100">
              {detailRows.map((row, i) => (
                <div
                  key={i}
                  className="grid gap-1 py-4 first:pt-0 sm:grid-cols-[minmax(0,10rem)_1fr] sm:gap-6 sm:py-5"
                >
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {row.label}
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <p className="mt-6 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-950">
              {t('confirmation.pendingNote')}
            </p>

            {waHref ? (
              <button
                type="button"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95"
                style={getReadablePrimaryButtonStyle(themeColor)}
                onClick={() => window.open(waHref, "_blank", "noopener,noreferrer")}
              >
                <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
                {t('confirmation.openWhatsApp', { phone: project.contactNo })}
              </button>
            ) : null}

            <button
              type="button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border px-5 py-3.5 text-sm font-semibold transition hover:bg-gray-50 active:brightness-95"
              style={{ borderColor: themeColor, color: themeColor }}
              onClick={() => navigate("/rent")}
            >
              <PackagePlus className="h-5 w-5 shrink-0" aria-hidden />
              {t('confirmation.rentAgain')}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
